import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { ArchitectInvite, EngineerInvite, CRMUser } from '../types';
import AppHeader from '../components/ui/AppHeader';
import {
  Users, UserPlus, Mail, Shield, Clock, CheckCircle, XCircle,
  Copy, ExternalLink, AlertTriangle, Send, X, RefreshCw, Link2, RotateCcw, KeyRound, ArrowLeft,
} from 'lucide-react';
import '../components/pm/pm-shared.css';
import './TeamManagement.css';
import UserAvatar from '../components/ui/UserAvatar';
import UnifiedInviteModal from '../components/admin/UnifiedInviteModal';
import { crmAuthedFetch } from '../utils/crmAuthedFetch';
import { isDesignerRole } from '../utils/userRoles';

const FUNCTIONS_URL = 'https://us-central1-lctf-projects.cloudfunctions.net';

const TeamManagement: React.FC = () => {
  const { user } = useAuth();
  const {
    crmUsers, architectInvites, engineerInvites, updateCRMUser,
    createArchitectInvite, revokeArchitectInvite,
    createEngineerInvite, revokeEngineerInvite,
  } = useCRM();

  type InviteTarget = 'architect' | 'engineer';

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<InviteTarget>('architect');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<{
    success: boolean; message: string;
    setupLink?: string; email?: string; portalUrl?: string;
  } | null>(null);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [regenResult, setRegenResult] = useState<{
    inviteId: string; email: string; setupLink: string;
    tempPassword?: string;
    emailSent: boolean; copied: boolean;
  } | null>(null);

  const [partnerResetLoading, setPartnerResetLoading] = useState<string | null>(null);
  const [partnerResetResult, setPartnerResetResult] = useState<{
    targetUid: string;
    email: string;
    displayName: string;
    emailSent: boolean;
    message?: string;
    error?: string;
    copied: boolean;
  } | null>(null);

  const employees = useMemo(() =>
    crmUsers.filter(u => u.role === 'employee' || u.role === 'admin').sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers]
  );

  const architects = useMemo(() =>
    crmUsers.filter(u => isDesignerRole(u.role)).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers]
  );

  const engineers = useMemo(() =>
    crmUsers.filter(u => u.role === 'engineer').sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [crmUsers]
  );

  const pendingInvites = useMemo(() => {
    const architectEmails = new Set(architects.map(a => a.email.toLowerCase()));
    return architectInvites.filter(i =>
      i.status === 'pending' && !architectEmails.has(i.email.toLowerCase())
    );
  }, [architectInvites, architects]);

  const pendingEngineerInvites = useMemo(() => {
    const engineerEmails = new Set(engineers.map(e => e.email.toLowerCase()));
    return engineerInvites.filter(i =>
      i.status === 'pending' && !engineerEmails.has(i.email.toLowerCase())
    );
  }, [engineerInvites, engineers]);

  const handleSendInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;

    setSending(true);
    setInviteResult(null);

    const createFn = inviteTarget === 'engineer' ? createEngineerInvite : createArchitectInvite;
    const functionName = inviteTarget === 'engineer' ? 'sendEngineerInvite' : 'sendArchitectInvite';

    try {
      const inviteId = await createFn({
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        invitedBy: user?.id || '',
        invitedByName: user?.name || '',
        status: 'pending',
      });

      const response = await crmAuthedFetch(`${FUNCTIONS_URL}/${functionName}`, {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim().toLowerCase(),
          name: inviteName.trim(),
          inviteId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.method === 'email') {
          setInviteResult({
            success: true,
            message: `Invite email sent to ${inviteEmail.trim()}`,
            setupLink: data.setupLink,
            email: inviteEmail.trim().toLowerCase(),
            portalUrl: 'https://clients.lctimberframes.com',
          });
        } else {
          setInviteResult({
            success: true,
            message: data.message || `Share the setup link securely with the ${inviteTarget}:`,
            setupLink: data.setupLink,
            email: inviteEmail.trim().toLowerCase(),
            portalUrl: data.portalUrl || 'https://clients.lctimberframes.com',
          });
        }
      } else {
        setInviteResult({ success: false, message: data.error || 'Failed to send invite' });
      }
    } catch (err) {
      console.error('Error sending invite:', err);
      setInviteResult({ success: false, message: 'Network error. Check your connection.' });
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setInviteName('');
    setInviteEmail('');
    setInviteResult(null);
  };

  const handleRevokeInvite = async (id: string, type: InviteTarget = 'architect') => {
    if (!window.confirm(`Revoke this invite? The ${type} will no longer be able to sign in.`)) return;
    if (type === 'engineer') {
      await revokeEngineerInvite(id);
    } else {
      await revokeArchitectInvite(id);
    }
  };

  const handleRevokeAccess = async (uid: string, role: string = 'architect') => {
    if (!window.confirm(`Revoke ${role} access? They will lose access to the portal.`)) return;
    await updateCRMUser(uid, { role: 'client' } as any);
  };

  const handleResendInvite = async (invite: ArchitectInvite | EngineerInvite, type: InviteTarget = 'architect') => {
    if (!invite.id) return;
    setRegenerating(invite.id);
    setRegenResult(null);

    const functionName = type === 'engineer' ? 'sendEngineerInvite' : 'sendArchitectInvite';

    try {
      const response = await crmAuthedFetch(`${FUNCTIONS_URL}/${functionName}`, {
        method: 'POST',
        body: JSON.stringify({
          email: invite.email,
          name: invite.name,
          inviteId: invite.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setRegenResult({
          inviteId: invite.id,
          email: invite.email,
          setupLink: data.setupLink || '',
          tempPassword: typeof data.tempPassword === 'string' ? data.tempPassword : '',
          emailSent: data.method === 'email',
          copied: false,
        });
      } else {
        console.error('Resend failed:', data);
      }
    } catch (err) {
      console.error('Error resending invite:', err);
    } finally {
      setRegenerating(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePartnerPasswordReset = async (u: CRMUser) => {
    setPartnerResetLoading(u.uid);
    setPartnerResetResult(null);
    try {
      const sendReset = httpsCallable(
        getFunctions(undefined, 'us-central1'),
        'sendPasswordResetLinkForPartner',
      );
      const { data } = await sendReset({ targetUid: u.uid });
      const d = data as {
        success?: boolean;
        method?: string;
        message?: string;
      };
      if (d.success) {
        setPartnerResetResult({
          targetUid: u.uid,
          email: u.email,
          displayName: u.displayName,
          emailSent: d.method === 'email',
          message: d.message,
          copied: false,
        });
      } else {
        setPartnerResetResult({
          targetUid: u.uid,
          email: u.email,
          displayName: u.displayName,
          emailSent: false,
          error: 'Unexpected response from server',
          copied: false,
        });
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setPartnerResetResult({
        targetUid: u.uid,
        email: u.email,
        displayName: u.displayName,
        emailSent: false,
        error: e?.message || 'Failed to send password reset',
        copied: false,
      });
    } finally {
      setPartnerResetLoading(null);
    }
  };

  const formatDate = (d: Date) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="tm-page">
      <AppHeader />
      <div className="tm-container">
        <Link to="/designer-portal" className="tm-back-link">
          <ArrowLeft size={14} aria-hidden />
          Design Portal
        </Link>

        <div className="tm-header">
          <div>
            <h1 className="tm-title">Team Management</h1>
            <p className="tm-subtitle">{employees.length + architects.length + engineers.length} team members</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="pm-btn pm-btn-primary" onClick={() => { setInviteTarget('architect'); setShowInviteModal(true); resetForm(); }}>
              <UserPlus size={14} /> Invite teammate
            </button>
          </div>
        </div>

        {/* Internal team */}
        <div className="tm-section">
          <h3 className="tm-section-title">
            <Shield size={14} /> Internal Team
            <span className="tm-section-count">{employees.length}</span>
          </h3>
          <div className="tm-members">
            {employees.map(u => (
              <div key={u.uid} className="tm-member-card">
                <UserAvatar className="pm-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} style={{ width: 36, height: 36, fontSize: 13 }} />
                <div className="tm-member-info">
                  <span className="tm-member-name">{u.displayName}</span>
                  <span className="tm-member-email">{u.email}</span>
                </div>
                <span className={`tm-role-badge tm-role-${u.role}`}>{u.role}</span>
                <span className="tm-member-date">Last active {formatDate(u.lastLogin)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Internal designers */}
        <div className="tm-section">
          <h3 className="tm-section-title">
            <Users size={14} /> Designers
            <span className="tm-section-count">{architects.length}</span>
          </h3>
          {architects.length === 0 && pendingInvites.length === 0 ? (
            <div className="tm-empty">
              <UserPlus size={28} />
              <p>No designers yet. Invite one to get started.</p>
            </div>
          ) : (
            <div className="tm-members">
              {architects.map(u => (
                <div key={u.uid} className="tm-pending-invite-block">
                  <div className="tm-member-card">
                    <UserAvatar className="pm-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} style={{ width: 36, height: 36, fontSize: 13 }} />
                    <div className="tm-member-info">
                      <span className="tm-member-name">{u.displayName}</span>
                      <span className="tm-member-email">{u.email}</span>
                    </div>
                    <span className="tm-role-badge tm-role-architect">Designer</span>
                    <span className="tm-member-date">Joined {formatDate(u.createdAt)}</span>
                    <button
                      type="button"
                      className="pm-btn pm-btn-sm tm-btn-regen"
                      onClick={() => handlePartnerPasswordReset(u)}
                      disabled={partnerResetLoading === u.uid}
                    >
                      {partnerResetLoading === u.uid
                        ? <><RefreshCw size={12} className="tm-spin" /> Sending...</>
                        : <><KeyRound size={12} /> Reset link</>}
                    </button>
                    <button
                      className="pm-btn pm-btn-sm"
                      style={{ color: '#DC2626', borderColor: '#FECACA' }}
                      onClick={() => handleRevokeAccess(u.uid)}
                    >
                      <XCircle size={12} /> Revoke
                    </button>
                  </div>
                  {partnerResetResult && partnerResetResult.targetUid === u.uid && (
                    <div className="tm-regen-result">
                      {partnerResetResult.error ? (
                        <div className="tm-result tm-result-error" style={{ margin: 0 }}>
                          <AlertTriangle size={14} />
                          <p style={{ margin: 0 }}>{partnerResetResult.error}</p>
                          <button type="button" className="pm-btn pm-btn-sm" onClick={() => setPartnerResetResult(null)}>
                            Dismiss
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="tm-regen-header">
                            <CheckCircle size={13} />
                            <span>
                              {partnerResetResult.emailSent
                                ? `Password reset email sent to ${partnerResetResult.email}`
                                : (partnerResetResult.message || 'Password reset email could not be sent.')}
                            </span>
                          </div>
                          <div className="tm-regen-actions" style={{ marginTop: 8 }}>
                            <button type="button" className="pm-btn pm-btn-sm" onClick={() => setPartnerResetResult(null)}>
                              Dismiss
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Pending invites */}
              {pendingInvites.map(inv => (
                <div key={inv.id} className="tm-pending-invite-block">
                  <div className="tm-member-card tm-member-pending">
                    <div className="pm-avatar" style={{ background: '#94A3B8', width: 36, height: 36, fontSize: 13 }}>
                      <Mail size={16} />
                    </div>
                    <div className="tm-member-info">
                      <span className="tm-member-name">{inv.name}</span>
                      <span className="tm-member-email">{inv.email}</span>
                    </div>
                    <span className="tm-role-badge tm-role-pending">
                      <Clock size={10} /> Pending
                    </span>
                    <span className="tm-member-date">Invited {formatDate(inv.createdAt)}</span>
                    <button
                      className="pm-btn pm-btn-sm tm-btn-regen"
                      onClick={() => handleResendInvite(inv)}
                      disabled={regenerating === inv.id}
                    >
                      {regenerating === inv.id
                        ? <><RefreshCw size={12} className="tm-spin" /> Sending...</>
                        : <><RotateCcw size={12} /> Resend</>}
                    </button>
                    <button
                      className="pm-btn pm-btn-sm"
                      style={{ color: '#DC2626', borderColor: '#FECACA' }}
                      onClick={() => handleRevokeInvite(inv.id!)}
                    >
                      <XCircle size={12} /> Revoke
                    </button>
                  </div>
                  {regenResult && regenResult.inviteId === inv.id && (
                    <div className="tm-regen-result">
                      <div className="tm-regen-header">
                        <CheckCircle size={13} />
                        <span>
                          {regenResult.emailSent
                            ? `Fresh invite email sent to ${inv.email}`
                            : `New credentials generated for ${inv.name}`}
                        </span>
                      </div>
                      {(regenResult.setupLink || regenResult.tempPassword) && (
                        <div className="tm-credentials-box">
                          <div className="tm-cred-row">
                            <span className="tm-cred-label">Email</span>
                            <span className="tm-cred-value">{regenResult.email}</span>
                          </div>
                          {regenResult.tempPassword && (
                            <div className="tm-cred-row">
                              <span className="tm-cred-label">Temporary password</span>
                              <code className="tm-cred-password">{regenResult.tempPassword}</code>
                            </div>
                          )}
                          {regenResult.setupLink && (
                            <div className="tm-cred-row">
                              <span className="tm-cred-label">Setup link</span>
                              <code className="tm-cred-password">{regenResult.setupLink}</code>
                            </div>
                          )}
                          <div className="tm-regen-actions">
                            <button
                              className="pm-btn pm-btn-primary pm-btn-sm"
                              onClick={() => {
                                const lines = [
                                  'Sign in at: https://clients.lctimberframes.com',
                                  `Email: ${regenResult.email}`,
                                ];
                                if (regenResult.tempPassword) lines.push(`Temporary password: ${regenResult.tempPassword}`);
                                if (regenResult.setupLink) lines.push(`Setup link: ${regenResult.setupLink}`);
                                copyToClipboard(lines.join('\n'));
                                setRegenResult({ ...regenResult, copied: true });
                              }}
                            >
                              <Copy size={12} /> {regenResult.copied ? 'Copied!' : 'Copy Credentials'}
                            </button>
                            <button className="pm-btn pm-btn-sm" onClick={() => setRegenResult(null)}>
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Engineers */}
        <div className="tm-section">
          <h3 className="tm-section-title">
            <Users size={14} /> Engineers
            <span className="tm-section-count">{engineers.length}</span>
          </h3>
          {engineers.length === 0 && pendingEngineerInvites.length === 0 ? (
            <div className="tm-empty">
              <UserPlus size={28} />
              <p>No engineers yet. Invite one to get started.</p>
            </div>
          ) : (
            <div className="tm-members">
              {engineers.map(u => (
                <div key={u.uid} className="tm-pending-invite-block">
                  <div className="tm-member-card">
                    <UserAvatar className="pm-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} style={{ width: 36, height: 36, fontSize: 13 }} />
                    <div className="tm-member-info">
                      <span className="tm-member-name">{u.displayName}</span>
                      <span className="tm-member-email">{u.email}</span>
                    </div>
                    <span className="tm-role-badge tm-role-engineer">engineer</span>
                    <span className="tm-member-date">Joined {formatDate(u.createdAt)}</span>
                    <button
                      type="button"
                      className="pm-btn pm-btn-sm tm-btn-regen"
                      onClick={() => handlePartnerPasswordReset(u)}
                      disabled={partnerResetLoading === u.uid}
                    >
                      {partnerResetLoading === u.uid
                        ? <><RefreshCw size={12} className="tm-spin" /> Sending...</>
                        : <><KeyRound size={12} /> Reset link</>}
                    </button>
                    <button
                      className="pm-btn pm-btn-sm"
                      style={{ color: '#DC2626', borderColor: '#FECACA' }}
                      onClick={() => handleRevokeAccess(u.uid, 'engineer')}
                    >
                      <XCircle size={12} /> Revoke
                    </button>
                  </div>
                  {partnerResetResult && partnerResetResult.targetUid === u.uid && (
                    <div className="tm-regen-result">
                      {partnerResetResult.error ? (
                        <div className="tm-result tm-result-error" style={{ margin: 0 }}>
                          <AlertTriangle size={14} />
                          <p style={{ margin: 0 }}>{partnerResetResult.error}</p>
                          <button type="button" className="pm-btn pm-btn-sm" onClick={() => setPartnerResetResult(null)}>
                            Dismiss
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="tm-regen-header">
                            <CheckCircle size={13} />
                            <span>
                              {partnerResetResult.emailSent
                                ? `Password reset email sent to ${partnerResetResult.email}`
                                : (partnerResetResult.message || 'Password reset email could not be sent.')}
                            </span>
                          </div>
                          <div className="tm-regen-actions" style={{ marginTop: 8 }}>
                            <button type="button" className="pm-btn pm-btn-sm" onClick={() => setPartnerResetResult(null)}>
                              Dismiss
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {pendingEngineerInvites.map(inv => (
                <div key={inv.id} className="tm-pending-invite-block">
                  <div className="tm-member-card tm-member-pending">
                    <div className="pm-avatar" style={{ background: '#94A3B8', width: 36, height: 36, fontSize: 13 }}>
                      <Mail size={16} />
                    </div>
                    <div className="tm-member-info">
                      <span className="tm-member-name">{inv.name}</span>
                      <span className="tm-member-email">{inv.email}</span>
                    </div>
                    <span className="tm-role-badge tm-role-pending">
                      <Clock size={10} /> Pending
                    </span>
                    <span className="tm-member-date">Invited {formatDate(inv.createdAt)}</span>
                    <button
                      className="pm-btn pm-btn-sm tm-btn-regen"
                      onClick={() => handleResendInvite(inv, 'engineer')}
                      disabled={regenerating === inv.id}
                    >
                      {regenerating === inv.id
                        ? <><RefreshCw size={12} className="tm-spin" /> Sending...</>
                        : <><RotateCcw size={12} /> Resend</>}
                    </button>
                    <button
                      className="pm-btn pm-btn-sm"
                      style={{ color: '#DC2626', borderColor: '#FECACA' }}
                      onClick={() => handleRevokeInvite(inv.id!, 'engineer')}
                    >
                      <XCircle size={12} /> Revoke
                    </button>
                  </div>
                  {regenResult && regenResult.inviteId === inv.id && (
                    <div className="tm-regen-result">
                      <div className="tm-regen-header">
                        <CheckCircle size={13} />
                        <span>
                          {regenResult.emailSent
                            ? `Fresh invite email sent to ${inv.email}`
                            : `New credentials generated for ${inv.name}`}
                        </span>
                      </div>
                      {(regenResult.setupLink || regenResult.tempPassword) && (
                        <div className="tm-credentials-box">
                          <div className="tm-cred-row">
                            <span className="tm-cred-label">Email</span>
                            <span className="tm-cred-value">{regenResult.email}</span>
                          </div>
                          {regenResult.tempPassword && (
                            <div className="tm-cred-row">
                              <span className="tm-cred-label">Temporary password</span>
                              <code className="tm-cred-password">{regenResult.tempPassword}</code>
                            </div>
                          )}
                          {regenResult.setupLink && (
                            <div className="tm-cred-row">
                              <span className="tm-cred-label">Setup link</span>
                              <code className="tm-cred-password">{regenResult.setupLink}</code>
                            </div>
                          )}
                          <div className="tm-regen-actions">
                            <button
                              className="pm-btn pm-btn-primary pm-btn-sm"
                              onClick={() => {
                                const lines = [
                                  'Sign in at: https://clients.lctimberframes.com',
                                  `Email: ${regenResult.email}`,
                                ];
                                if (regenResult.tempPassword) lines.push(`Temporary password: ${regenResult.tempPassword}`);
                                if (regenResult.setupLink) lines.push(`Setup link: ${regenResult.setupLink}`);
                                copyToClipboard(lines.join('\n'));
                                setRegenResult({ ...regenResult, copied: true });
                              }}
                            >
                              <Copy size={12} /> {regenResult.copied ? 'Copied!' : 'Copy Credentials'}
                            </button>
                            <button className="pm-btn pm-btn-sm" onClick={() => setRegenResult(null)}>
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invite history */}
        {(architectInvites.length > 0 || engineerInvites.length > 0) && (
          <div className="tm-section">
            <h3 className="tm-section-title">
              <Clock size={14} /> Invite History
              <span className="tm-section-count">{architectInvites.length + engineerInvites.length}</span>
            </h3>
            <div className="tm-invite-list">
              {[
                ...architectInvites.map(inv => ({ ...inv, _type: 'architect' as const })),
                ...engineerInvites.map(inv => ({ ...inv, _type: 'engineer' as const })),
              ]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map(inv => (
                <div key={`${inv._type}-${inv.id}`} className="tm-invite-row">
                  <span className="tm-invite-name">{inv.name}</span>
                  <span className="tm-invite-email">{inv.email}</span>
                  <span className={`tm-role-badge tm-role-${inv._type}`} style={{ fontSize: 10, padding: '1px 6px' }}>{inv._type}</span>
                  <span className={`tm-invite-status tm-invite-${inv.status}`}>
                    {inv.status === 'pending' && <Clock size={10} />}
                    {inv.status === 'accepted' && <CheckCircle size={10} />}
                    {inv.status === 'revoked' && <XCircle size={10} />}
                    {inv.status}
                  </span>
                  <span className="tm-invite-date">{formatDate(inv.createdAt)}</span>
                  <span className="tm-invite-by">by {inv.invitedByName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showInviteModal && (
        <UnifiedInviteModal
          defaultRole="designer"
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
};

export default TeamManagement;
